import React, { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import serverURL from "../config";
import { toast } from "react-toastify";
import SingleQuesModal from "../components/SingleQuesModal";
import BulkQuesModal from "../components/BulkQuesModal";

const CategoryDetails = () => {
    const { id, name } = useParams();
    const location = useLocation();
    const { description, isActive } = location.state || {};

    const [modal, setModal] = useState({ type: null, isVisible: false });
    const [questions, setQuestions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentQuestion, setCurrentQuestion] = useState(null);

    const openModal = (type, question = null) => {
        setCurrentQuestion(question);
        setModal({ type, isVisible: true });
    };

    const closeModal = () => {
        setModal({ type: null, isVisible: false });
        setCurrentQuestion(null);
    };

    const handleAddQuestion = async (questionData) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.post(`${serverURL}/questions/create`, questionData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            console.log('Question added:', response.data);
            fetchQuestions(); // Refresh the questions list after adding a new question
            closeModal();
        } catch (error) {
            console.error('Failed to add question:', error);
        }
    };

    const fetchQuestions = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${serverURL}/questions/category/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setQuestions(response.data);
        } catch (error) {
            console.error('Failed to fetch questions:', error);
            toast.error('Failed to fetch questions');
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        const token = localStorage.getItem('token');
        try {
            await axios.delete(`${serverURL}/questions/${questionId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setQuestions(questions.filter(question => question.id !== questionId));
            toast.success('Question deleted successfully');
        } catch (error) {
            console.error('Failed to delete question:', error);
            toast.error('Failed to delete question');
        }
    };

    const handleUpdateQuestion = async () => {
        const token = localStorage.getItem('token');
        try {
            if (currentQuestion.question_type === "Programming") {
                // Prepare the data for the programming question update
                const programmingData = {
                    question_text: currentQuestion.question_text,
                    points: currentQuestion.points,
                    programming_details: {
                        programming_language: currentQuestion.programming_details.programming_language,
                        input_format: currentQuestion.programming_details.input_format,
                        output_format: currentQuestion.programming_details.output_format,
                        sample_input: currentQuestion.programming_details.sample_input,
                        sample_output: currentQuestion.programming_details.sample_output,
                        time_limit: currentQuestion.programming_details.time_limit,
                        memory_limit: currentQuestion.programming_details.memory_limit,
                    },
                    test_cases: currentQuestion.test_cases,
                };

                const response = await axios.put(`${serverURL}/questions/${currentQuestion.id}`, programmingData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                console.log('Programming question updated:', response.data);
            } else {
                // Handle other question types if necessary
                const response = await axios.put(`${serverURL}/questions/${currentQuestion.id}`, currentQuestion, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });
                console.log('Question updated:', response.data);
            }
            fetchQuestions();
            closeModal();
        } catch (error) {
            console.error('Failed to update question:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentQuestion((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleOptionChange = (index, e) => {
        const { name, value } = e.target;
        const updatedOptions = [...currentQuestion.options];
        updatedOptions[index][name] = value;
        setCurrentQuestion((prev) => ({
            ...prev,
            options: updatedOptions,
        }));
    };

    const handleCorrectOptionChange = (index) => {
        const updatedOptions = currentQuestion.options.map((option, i) => ({
            ...option,
            is_correct: i === index,
        }));
        setCurrentQuestion((prev) => ({
            ...prev,
            options: updatedOptions,
        }));
    };

    const filteredQuestions = questions.filter(question =>
        question.question_text.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.id.toString().includes(searchTerm)
    );

    useEffect(() => {
        fetchQuestions();
    }, [id]);

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">
            <header className="sticky top-0 bg-white shadow z-10">
                <Header headerTitle={"Category Details"} />
            </header>
            <main className="flex-grow p-6">
                <div className="bg-white p-6 rounded-lg shadow-lg relative w-full md:w-3/4 mx-auto">
                    <div className={`absolute top-2 right-2 text-white text-sm font-bold py-1 px-2 rounded ${isActive ? 'bg-green-500' : 'bg-red-500'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                    </div>
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl font-bold">{decodeURIComponent(name)}</h2>
                    </div>
                    <div>
                        <p className="text-lg">
                            <strong>Description:</strong>{" "}
                            <span className="text-gray-700">{description}</span>
                        </p>
                    </div>
                    <div className="flex my-4 gap-3">
                        <button
                            className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                            onClick={() => openModal("add")}
                        >
                            Add Question
                        </button>
                        <button
                            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                            onClick={() => openModal("bulk")}
                        >
                            Bulk Upload Question
                        </button>
                    </div>
                    <div className="my-4">
                        <input
                            type="text"
                            placeholder="Search questions by ID or text..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border p-2 rounded w-full"
                        />
                    </div>
                    <div className="mb-4">
                        <h3 className="text-xl font-bold">Questions({filteredQuestions.length}):</h3>
                        {filteredQuestions.length > 0 ? (
                            filteredQuestions.map((question) => (
                                <div key={question.id} className="mb-4">
                                    <div className="flex items-center justify-between">
                                        <h4 className="font-semibold">ID: {question.id}</h4>
                                        <p className={`font-semibold p-1 rounded ${question.difficulty_level === 'easy' ? 'bg-green-300' : question.difficulty_level === 'medium' ? 'bg-yellow-300' : 'bg-red-300'}`}>
                                            Level: {question.difficulty_level}
                                        </p>
                                    </div>
                                    <p><strong>Question:</strong> {question.question_text}</p>

                                    {question.question_type === "Programming" && question.programming_details ? (
                                        <div className="mt-2 p-4 border border-gray-300 rounded bg-gray-50">
                                            <h5 className="font-semibold">Programming Details:</h5>
                                            <p><strong>Input Format:</strong> {question.programming_details.input_format}</p>
                                            <p><strong>Output Format:</strong> {question.programming_details.output_format}</p>
                                            <p><strong>Sample Input:</strong> {question.programming_details.sample_input}</p>
                                            <p><strong>Sample Output:</strong> {question.programming_details.sample_output}</p>
                                            <p><strong>Programming Language:</strong> {question.programming_details.programming_language}</p>
                                        </div>
                                    ) : null}

                                    {question.question_type !== "Programming" && (
                                        <>
                                            <strong>Options:</strong>
                                            <ul>
                                                {Array.isArray(question.options) && question.options.length > 0 ? (
                                                    question.options.map((option) => (
                                                        <li key={option.id}>
                                                            {option.option_text} {option.is_correct ? "✅" : null}
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li>No options available</li>
                                                )}
                                            </ul>
                                        </>
                                    )}

                                    <div className="flex gap-2 mt-2">
                                        <button
                                            onClick={() => openModal("edit", question)}
                                            className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteQuestion(question.id)}
                                            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                    <hr className="my-2" />
                                </div>
                            ))
                        ) : (
                            <p className="text-lg text-gray-700">No questions to display</p>
                        )}
                    </div>
                </div>
            </main>
            <Footer />

            {modal.isVisible && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-1/2 max-h-[80vh] overflow-y-auto pr-4">
                        <div className="flex justify-between items-start mb-4">
                            {/* <h2 className="text-xl font-bold">Edit Question</h2> */}
                            <button onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 3rem)' }}>
                            {modal.type === "add" && <SingleQuesModal onClose={closeModal} onSubmit={handleAddQuestion} categoryId={id} />}
                            {modal.type === "bulk" && <BulkQuesModal onClose={closeModal} categoryId={id} />}
                            {modal.type === "edit" && currentQuestion && (
                                <>
                                    {currentQuestion.question_type === "Programming" ? (
                                        <>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Question:</label>
                                                <input
                                                    type="text"
                                                    name="question_text"
                                                    value={currentQuestion.question_text}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Points:</label>
                                                <input
                                                    type="number"
                                                    name="points"
                                                    value={currentQuestion.points}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Programming Details:</h3>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Programming Language:</label>
                                                <input
                                                    type="text"
                                                    name="programming_language"
                                                    value={currentQuestion.programming_details.programming_language}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Input Format:</label>
                                                <input
                                                    type="text"
                                                    name="input_format"
                                                    value={currentQuestion.programming_details.input_format}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Output Format:</label>
                                                <input
                                                    type="text"
                                                    name="output_format"
                                                    value={currentQuestion.programming_details.output_format}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Sample Input:</label>
                                                <input
                                                    type="text"
                                                    name="sample_input"
                                                    value={currentQuestion.programming_details.sample_input}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Sample Output:</label>
                                                <input
                                                    type="text"
                                                    name="sample_output"
                                                    value={currentQuestion.programming_details.sample_output}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Time Limit (ms):</label>
                                                <input
                                                    type="number"
                                                    name="time_limit"
                                                    value={currentQuestion.programming_details.time_limit}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Memory Limit (MB):</label>
                                                <input
                                                    type="number"
                                                    name="memory_limit"
                                                    value={currentQuestion.programming_details.memory_limit}
                                                    onChange={(e) => handleInputChange(e)}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Test Cases:</h3>
                                            {Array.isArray(currentQuestion.test_cases) && currentQuestion.test_cases.map((testCase, index) => (
                                                <div key={index} className="mb-4 mr-4">
                                                    <label className="block mb-1">Input Data:</label>
                                                    <input
                                                        type="text"
                                                        name="input_data"
                                                        value={testCase.input_data}
                                                        onChange={(e) => {
                                                            const updatedTestCases = [...currentQuestion.test_cases];
                                                            updatedTestCases[index].input_data = e.target.value;
                                                            setCurrentQuestion((prev) => ({
                                                                ...prev,
                                                                test_cases: updatedTestCases,
                                                            }));
                                                        }}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                    <label className="block mb-1">Expected Output:</label>
                                                    <input
                                                        type="text"
                                                        name="expected_output"
                                                        value={testCase.expected_output}
                                                        onChange={(e) => {
                                                            const updatedTestCases = [...currentQuestion.test_cases];
                                                            updatedTestCases[index].expected_output = e.target.value;
                                                            setCurrentQuestion((prev) => ({
                                                                ...prev,
                                                                test_cases: updatedTestCases,
                                                            }));
                                                        }}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Question:</label>
                                                <input
                                                    type="text"
                                                    name="question_text"
                                                    value={currentQuestion.question_text}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <div className="mb-4 mr-4">
                                                <label className="block mb-1">Points:</label>
                                                <input
                                                    type="number"
                                                    name="points"
                                                    value={currentQuestion.points}
                                                    onChange={handleInputChange}
                                                    className="border p-2 rounded w-full"
                                                />
                                            </div>
                                            <h3 className="font-bold mb-2">Options:</h3>
                                            {Array.isArray(currentQuestion.options) && currentQuestion.options.map((option, index) => (
                                                <div key={index} className="flex items-center mb-2 mr-4">
                                                    <input
                                                        type="text"
                                                        name="option_text"
                                                        value={option.option_text}
                                                        onChange={(e) => handleOptionChange(index, e)}
                                                        className="border p-2 rounded w-full"
                                                    />
                                                    <label className="ml-2 flex items-center">
                                                        <input
                                                            type="radio"
                                                            checked={option.is_correct}
                                                            onChange={() => handleCorrectOptionChange(index)}
                                                        />
                                                        <span className="ml-1">Correct</span>
                                                    </label>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    <div className="flex justify-between">
                                        <button
                                            onClick={handleUpdateQuestion}
                                            className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                                        >
                                            Update Question
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="bg-gray-500 text-white p-2 rounded hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoryDetails;
